import type { Tile } from "../engine";
import {
  cardinalNeighbors,
  connectedRoadDirections,
  isDirectionInBounds,
  isLandTile,
  isSeaTile,
  mapHeight,
  neighborInDirection,
  offsetInDirection,
  perpendicularDirections,
} from "./roadBridgeNeighbors";
import { ROAD_CONNECT_E, ROAD_CONNECT_N, ROAD_CONNECT_S, ROAD_CONNECT_W } from "./roadConnectFlags";
import type { RoadAutotileKind } from "./roadStripeKinds";

const STRAIGHT_KINDS = new Set<RoadAutotileKind>(["straight", "deadend"]);

function roadAxisMask({
  kind,
  mask,
  neighbors,
  x,
  y,
  width,
  height,
}: {
  kind: RoadAutotileKind;
  mask: number;
  neighbors: ReturnType<typeof cardinalNeighbors>;
  x: number;
  y: number;
  width: number;
  height: number;
}): number | null {
  if (kind === "straight" || kind === "deadend") {
    if (mask & (ROAD_CONNECT_N | ROAD_CONNECT_S)) return ROAD_CONNECT_N | ROAD_CONNECT_S;
    if (mask & (ROAD_CONNECT_E | ROAD_CONNECT_W)) return ROAD_CONNECT_E | ROAD_CONNECT_W;
  }

  const { north, east, south, west } = neighbors;
  if (isSeaTile(west) && isSeaTile(east)) return ROAD_CONNECT_N | ROAD_CONNECT_S;
  if (isSeaTile(north) && isSeaTile(south)) return ROAD_CONNECT_E | ROAD_CONNECT_W;
  if (x > 0 && isSeaTile(west)) return ROAD_CONNECT_N | ROAD_CONNECT_S;
  if (x < width - 1 && isSeaTile(east)) return ROAD_CONNECT_N | ROAD_CONNECT_S;
  if (y > 0 && isSeaTile(north)) return ROAD_CONNECT_E | ROAD_CONNECT_W;
  if (y < height - 1 && isSeaTile(south)) return ROAD_CONNECT_E | ROAD_CONNECT_W;
  return null;
}

function hasLandNeighbor({
  tiles,
  width,
  x,
  y,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): boolean {
  const height = mapHeight(tiles, width);
  const neighbors = cardinalNeighbors({ tiles, width, x, y });
  return [
    { tile: neighbors.north, direction: ROAD_CONNECT_N },
    { tile: neighbors.east, direction: ROAD_CONNECT_E },
    { tile: neighbors.south, direction: ROAD_CONNECT_S },
    { tile: neighbors.west, direction: ROAD_CONNECT_W },
  ].some(({ tile, direction }) => {
    if (!isDirectionInBounds({ direction, x, y, width, height })) return false;
    return isLandTile(tile);
  });
}

function isStraightOverWater({
  tiles,
  width,
  x,
  y,
  kind,
  mask,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
  kind: RoadAutotileKind;
  mask: number;
}): boolean {
  if (!STRAIGHT_KINDS.has(kind)) return false;

  const height = mapHeight(tiles, width);
  const axisMask = roadAxisMask({
    kind,
    mask,
    neighbors: cardinalNeighbors({ tiles, width, x, y }),
    x,
    y,
    width,
    height,
  });
  if (axisMask === null) return false;

  const [sideA, sideB] = perpendicularDirections(axisMask);
  return [sideA, sideB].every((direction) => {
    if (!isDirectionInBounds({ direction, x, y, width, height })) return false;
    const tile = neighborInDirection({
      direction,
      neighbors: cardinalNeighbors({ tiles, width, x, y }),
    });
    return isSeaTile(tile);
  });
}

function isConnectedToBridge({
  mask,
  x,
  y,
  bridges,
}: {
  mask: number;
  x: number;
  y: number;
  bridges: Map<string, boolean>;
}): boolean {
  return connectedRoadDirections(mask).some((direction) => {
    const next = offsetInDirection({ direction, x, y });
    return bridges.get(`${next.x},${next.y}`) ?? false;
  });
}

export function evaluateBridgeTile({
  tiles,
  width,
  x,
  y,
  kind,
  mask,
  bridges,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
  kind: RoadAutotileKind;
  mask: number;
  bridges: Map<string, boolean>;
}): boolean {
  if (isStraightOverWater({ tiles, width, x, y, kind, mask })) return true;
  if (hasLandNeighbor({ tiles, width, x, y })) return false;
  return isConnectedToBridge({ mask, x, y, bridges });
}
